import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { useConfirm } from '@/shared/components/confirm';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Skeleton } from '@/shared/ui/skeleton';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useCatalog, saveCatalogItem, type CatalogItem } from '../api/use-catalog';

/** CRUD admin do catálogo de módulos que a fixe oferece — reflete no wizard de cliente (step Produtos). */
export function ModulesPage() {
  const { user } = useAuth();
  const { data = [], isLoading } = useCatalog('module');
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<CatalogItem | null>(null);
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['catalog', 'module'] });

  const openCreate = () => {
    setEditItem(null);
    setName('');
    setActive(true);
    setDialogOpen(true);
  };

  const openEdit = (item: CatalogItem) => {
    setEditItem(item);
    setName(item.name);
    setActive(item.active);
    setDialogOpen(true);
  };

  const onSubmit = async () => {
    if (!name.trim()) {
      toast.error('Informe o nome do módulo.');
      return;
    }
    setSaving(true);
    try {
      await saveCatalogItem({
        action: editItem ? 'edit' : 'insert',
        id: editItem?._id,
        kind: 'module',
        name: name.trim(),
        active,
        order: editItem?.order ?? data.length,
      });
      toast.success(editItem ? 'Módulo atualizado.' : 'Módulo criado.');
      setDialogOpen(false);
      invalidate();
    } catch {
      toast.error('Falha ao salvar módulo.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: CatalogItem) => {
    const ok = await confirm({
      title: 'Remover módulo',
      description: `Remover "${item.name}" do catálogo? Clientes que já têm esse módulo marcado não são afetados.`,
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await saveCatalogItem({ action: 'deleted', id: item._id, kind: 'module' });
      toast.success('Módulo removido.');
      invalidate();
    } catch {
      toast.error('Falha ao remover módulo.');
    }
  };

  const toggleActive = async (item: CatalogItem) => {
    try {
      await saveCatalogItem({ action: 'edit', id: item._id, kind: 'module', name: item.name, active: !item.active, order: item.order });
      invalidate();
    } catch {
      toast.error('Falha ao atualizar módulo.');
    }
  };

  if (!user || user.role !== 'Admin') {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <PageHeader
        title="Módulos"
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" /> Novo módulo
          </Button>
        }
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-2xl" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <EmptyState icon={Package} title="Nenhum módulo" description="Cadastre o primeiro módulo do catálogo." />
      ) : (
        <div className="space-y-2">
          {data.map((item) => (
            <Card key={item._id} className="flex items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-3">
                <Package className="size-4 text-muted-foreground" />
                <span className={item.active ? '' : 'text-muted-foreground line-through'}>{item.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={item.active} onCheckedChange={() => void toggleActive(item)} />
                <ActionIconButton label="Editar módulo" onClick={() => openEdit(item)}>
                  <Pencil className="size-4" />
                </ActionIconButton>
                <ActionIconButton label="Remover módulo" onClick={() => remove(item)} className="text-destructive">
                  <Trash2 className="size-4" />
                </ActionIconButton>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Editar módulo' : 'Novo módulo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="module-name">Nome</Label>
              <Input id="module-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Fidelidade 360" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <p className="text-sm font-medium">Ativo</p>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void onSubmit()} loading={saving}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
